import { useEffect, useMemo, useState } from "react";
import { Cpu } from "lucide-react";

import PilotageField from "@/components/pilotage/PilotageField";
import AppErrorState from "@/components/ui/AppErrorState";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/api";
import { getLlmSettings, updateLlmSettings } from "@/lib/settingsApi";

const PROVIDER_OPTIONS = [
  { value: "google_ai", label: "Canal IA standard" },
  { value: "vertex_ai", label: "Canal IA cloud" },
];

const MODEL_OPTIONS = [
  {
    value: "gemini-2.5-flash",
    label: "Gemini 2.5 Flash — Par défaut",
    description: "Rapide et économique, recommandé pour les imports CV courants.",
  },
  {
    value: "gemini-3.8-flash",
    label: "Gemini 3.8 Flash",
    description: "Plus adapté aux CV complexes et aux documents longs.",
  },
  {
    value: "gemini-4-argon",
    label: "Gemini 4 Argon",
    description: "Raisonnement avancé, plus lent et plus coûteux.",
  },
];

const DEFAULT_VALUES = {
  llm_provider: "google_ai",
  llm_model: "gemini-2.5-flash",
};

const LlmSettingsSection = () => {
  const [values, setValues] = useState(DEFAULT_VALUES);
  const [initialValues, setInitialValues] = useState(DEFAULT_VALUES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getLlmSettings();
        const nextValues = {
          llm_provider: response?.llm_provider || DEFAULT_VALUES.llm_provider,
          llm_model: response?.llm_model || DEFAULT_VALUES.llm_model,
        };
        setValues(nextValues);
        setInitialValues(nextValues);
      } catch (requestError) {
        setError(getApiErrorMessage(requestError, "Impossible de charger la configuration LLM."));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, []);

  const hasChanges = useMemo(
    () =>
      values.llm_provider !== initialValues.llm_provider ||
      values.llm_model !== initialValues.llm_model,
    [initialValues.llm_model, initialValues.llm_provider, values.llm_model, values.llm_provider],
  );
  const selectedModel = MODEL_OPTIONS.find((option) => option.value === values.llm_model);

  const onChange = (field) => (event) => {
    setValues((previous) => ({
      ...previous,
      [field]: event.target.value,
    }));
    setSuccessMessage("");
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!hasChanges) {
      return;
    }

    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await updateLlmSettings(values);
      const nextValues = {
        llm_provider: response?.llm_provider || values.llm_provider,
        llm_model: response?.llm_model || values.llm_model,
      };
      setValues(nextValues);
      setInitialValues(nextValues);
      setSuccessMessage("Configuration IA enregistree.");
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Impossible d'enregistrer la configuration LLM."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4 rounded-3xl border border-white/10 glass p-6" data-testid="llm-settings-section">
      <div className="flex items-start gap-3">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-2">
          <Cpu className="h-4 w-4 text-slate-200" />
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Configuration IA
          </p>
          <h2 className="mt-1 text-lg font-heading font-bold text-white">Canal et profil d'analyse</h2>
          <p className="mt-1 text-sm text-slate-400">
            Choisissez le canal et le profil d'analyse utilises pour le pipeline CV.
          </p>
        </div>
      </div>

      {error ? <AppErrorState message={error} /> : null}
      {successMessage ? (
        <p className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
          {successMessage}
        </p>
      ) : null}

      <form className="grid gap-4 md:grid-cols-2" onSubmit={onSubmit}>
        <PilotageField label="Canal IA" htmlFor="llm-provider">
          <select
            id="llm-provider"
            value={values.llm_provider}
            onChange={onChange("llm_provider")}
            disabled={loading || saving}
            data-testid="llm-provider-select"
            className="w-full rounded-xl border border-white/10 bg-slate-950/60 px-3 py-2 text-sm text-white focus:border-lenvia-blue/60 focus:outline-none"
          >
            {PROVIDER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </PilotageField>

        <PilotageField label="Profil d'analyse" htmlFor="llm-model">
          <select
            id="llm-model"
            value={values.llm_model}
            onChange={onChange("llm_model")}
            disabled={loading || saving}
            data-testid="llm-model-select"
            className="w-full rounded-xl border border-white/10 bg-slate-950/60 px-3 py-2 text-sm text-white focus:border-lenvia-blue/60 focus:outline-none"
          >
            {MODEL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs leading-5 text-slate-400" aria-live="polite">
            {selectedModel?.description || "Modèle Gemini configuré pour les prochains imports CV."}
          </p>
        </PilotageField>

        <div className="md:col-span-2">
          <Button
            type="submit"
            className="btn-primary"
            disabled={loading || saving || !hasChanges}
            data-testid="llm-settings-save-button"
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      </form>
    </section>
  );
};

export default LlmSettingsSection;
